import { StorageService } from './../../../services/storage/storage.service';
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChildren,
  QueryList,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AbstractControl, ValidatorFn, Validators } from '@angular/forms';
import { SharedService } from './../../../services/shared/shared.service';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import { AdvancedFiltersComponent } from './advanced-filters/advanced-filters.component';
import { CustomAdvancedFiltersComponent } from './custom-advanced-filters/custom-advanced-filters.component';
import { RequiresOne } from '../../../shared/validators/requires-one.validator';
import {
  DateRangeValidationMessages,
  DateRangeValidators,
  LexileRangeValidationMessages,
  LexileRangeValidators,
  PointRangeValidationMessages,
  PointRangeValidators,
} from '../../../shared/validators/search-range.validators';

enum Filters {
  DateItemsAdded = 1,
  Libraries = 2,
  YearOfPublication = 3,
  Formats = 4,
  AudienceLevel = 5,
  Lexile = 6,
  Languages = 7,
  AcceleratedReaderInterestLevel = 8,
  MaterialType = 9,
  AcceleratedReaderReadingLevel = 10,
  AcceleratedReaderTitlePointValue = 11,
  ReadingProgram = 12,
  Awards = 13,
  ReadingCountsReadingLevel = 14,
  ReadingCountsTitlePointValue = 15,
  HasOCLC = 16,
  ReadingCountsInterestLevel = 17,
  Age = 18,
  Grade = 19,
}

export class NumberValidators {
  static isNumberCheck(): ValidatorFn {
    return (c: AbstractControl): { [key: string]: boolean } | null => {
      const number = /^[.\d]+$/.test(c.value) ? +c.value : NaN;
      if (number !== number) {
        return { value: true };
      }

      return null;
    };
  }
}

@Component({
  selector: 'refine-search',
  templateUrl: './refine-search.component.html',
  styleUrls: ['./refine-search.component.scss'],
  providers: [{ provide: ANIMATION_MODULE_TYPE, useValue: 'BrowserAnimations' }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RefineSearchComponent implements OnChanges, OnDestroy {
  @Input() embeddedComponent = false;
  @Input() availableFilters: any;
  @Output() filterChangeEE = new EventEmitter<any>();

  @ViewChildren(AdvancedFiltersComponent)
  advancedFilterComponents!: QueryList<AdvancedFiltersComponent>;
  @ViewChildren(CustomAdvancedFiltersComponent)
  customAdvancedFilterComponents!: QueryList<CustomAdvancedFiltersComponent>;

  expandCollapse = false;

  // post body variables and arrays
  private _scopingLevel = -1;
  private _languagesArray: any = [];
  private _pubYearsArray: any = [];
  private _formatsArray: any = [];
  private _materialTypesArray: any = [];
  private _audiencesArray: any = [];
  private _acceleratedReaderInterestLevel: any = [];
  private _acceleratedReaderReadingLevel: any = [];
  private _acceleratedReaderTitlePointValue: any = [];
  private _readingCountsReadingLevel: any = [];
  private _readingCountsTitlePointValue: any = [];
  private _readingCountsInterestLevel: any = [];
  private _hasOclcNumberArray: any = [];
  private _lexilesArray: any = [];
  private _dateItemAddedArray: any = [];
  private _awardsArray: any = [];
  private _readingProgramsArray: any = [];
  private _clcdAgeLevel: any = [];
  private _clcdGradeLevel: any = [];

  constructor(private _storageService: StorageService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.availableFilters?.currentValue) {
      this.availableFilters = changes.availableFilters.currentValue;
      this._initialFilterArray(this.availableFilters.filters);
    }
  }

  ngOnDestroy(): void {
    this._storageService.remove('queryYPubBox');
    this._storageService.remove('queryRLevelBox');
  }

  private _initialFilterArray(filters): void {
    filters?.forEach((filter) => {
      switch (filter.filterId) {
        case Filters.DateItemsAdded:
          this._dateItemAddedArray = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value']);
          //Fix backend sending -1 or '' when no dateItem values
          this._dateItemAddedArray = this._dateItemAddedArray.filter((v) =>
            SharedService.isNotEmpty(v),
          );
          break;
        case Filters.Libraries:
          this._scopingLevel = filter?.dropdownFields.find((x) => x.selected == true)?.value;
          break;
        case Filters.YearOfPublication:
          this._pubYearsArray = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value'])
            .concat(
              filter?.rangeFields
                .filter((r) => SharedService.isNotEmpty(r.from))
                .map((r) => `${r.from}<=>${r.to}`),
            );
          break;
        case Filters.Formats:
          this._formatsArray = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value']);
          break;
        case Filters.AudienceLevel:
          this._audiencesArray = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value']);
          break;
        case Filters.Lexile:
          this._lexilesArray = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value'])
            .concat(
              filter?.rangeFields
                .filter((r) => SharedService.isNotEmpty(r.from))
                .map((r) => `${r.from}<=>${r.to}`),
            );
          break;
        case Filters.Languages:
          this._languagesArray = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value']);
          break;
        case Filters.AcceleratedReaderInterestLevel:
          this._acceleratedReaderInterestLevel = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value']);
          break;
        case Filters.MaterialType:
          this._materialTypesArray = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value']);
          break;
        case Filters.AcceleratedReaderReadingLevel:
          this._acceleratedReaderReadingLevel = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value'])
            .concat(
              filter?.rangeFields
                .filter((r) => SharedService.isNotEmpty(r.from))
                .map((r) => `${r.from}<=>${r.to}`),
            );
          break;
        case Filters.AcceleratedReaderTitlePointValue:
          this._acceleratedReaderTitlePointValue = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value'])
            .concat(
              filter?.rangeFields
                .filter((r) => SharedService.isNotEmpty(r.from))
                .map((r) => `${r.from}<=>${r.to}`),
            );
          break;
        case Filters.ReadingProgram:
          this._readingProgramsArray = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value']);
          break;
        case Filters.Awards:
          this._awardsArray = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value']);
          break;
        case Filters.ReadingCountsReadingLevel:
          this._readingCountsReadingLevel = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value'])
            .concat(
              filter?.rangeFields
                .filter((r) => SharedService.isNotEmpty(r.from))
                .map((r) => `${r.from}<=>${r.to}`),
            );
          break;
        case Filters.ReadingCountsTitlePointValue:
          this._readingCountsTitlePointValue = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value'])
            .concat(
              filter?.rangeFields
                .filter((r) => SharedService.isNotEmpty(r.from))
                .map((r) => `${r.from}<=>${r.to}`),
            );
          break;
        case Filters.HasOCLC:
          this._hasOclcNumberArray = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value']);
          break;
        case Filters.ReadingCountsInterestLevel:
          this._readingCountsInterestLevel = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value']);
          break;
        case Filters.Age:
          this._clcdAgeLevel = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value']);
          break;
        case Filters.Grade:
          this._clcdGradeLevel = filter?.dropdownFields
            ?.filter((x) => x.selected)
            .map((x) => x['value']);
          break;
      }
    });

    if (!filters) {
      this._dateItemAddedArray = [];
      this._scopingLevel = -1;
    }
  }

  tooltip(filterId: number): string {
    switch (filterId) {
      case 3:
        return 'Enter a date range (1999 - 2005) or use an asterisk to filter before or after a date. For example: To filter for titles published after 1999, enter 1999 - *. To filter by a single year, enter the year in one box and leave the other box blank. ';
      case 6:
        return 'Enter a range (200 - 450) or use an asterisk to filter before or after a lexile. For example: To filter for lexiles before 199, enter * - 199. To filter for lexiles after 199, enter 199 - *. ';
      case 11:
      case 14:
        return 'Enter a range (2.0 - 7.5) or use an asterisk to filter before or after a reading level. For example: To filter for reading level before 2.0, enter * - 2.0. To filter for reading level after 2.0, enter 2.0 - *.';
      case 10:
      case 15:
        return 'Enter a range (2.0 - 7.5) or use an asterisk to filter before or after a point level. For example: To filter for point level before 2.0, enter * - 2.0. To filter for point level after 2.0, enter 2.0 - *.';
      default:
        return `Missing ${filterId}`;
    }
  }
  getGroupValidators(filterId: number) {
    if (filterId === 3) {
      return filterId === 3 ? [RequiresOne('from', 'to')] : [];
    }
  }
  getValidators(filterId: number) {
    switch (filterId) {
      case 3:
        return DateRangeValidators;
      case 6:
        return LexileRangeValidators;
      case 11:
      case 14:
      case 10:
      case 15:
        return PointRangeValidators;
      default:
        return [Validators.required];
    }
  }

  getValidationMessages(filterId: number) {
    let validationMessage;
    if (filterId === 3) {
      validationMessage = DateRangeValidationMessages;
    } else if (filterId === 6) {
      validationMessage = LexileRangeValidationMessages;
    } else if ([10, 11, 14, 15].some((v) => v === filterId)) {
      validationMessage = PointRangeValidationMessages;
    }
    return validationMessage;
  }

  selectFiltersDate(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this._dateItemAddedArray = [];
    this._dateItemAddedArray.push(value);
    this.filterChangeEE.emit(this.getFilters());
  }

  selectFiltersLibraries(event: Event) {
    const element = +(event.target as HTMLSelectElement).value;
    this._scopingLevel = element;
    this.filterChangeEE.emit(this.getFilters());
  }

  selectFilter(event) {
    switch (event.filter) {
      case 'forAudiences':
        this._audiencesArray = event.value;
        break;
      case 'languages':
        this._languagesArray = event.value;
        break;
      case 'materialTypes':
        this._materialTypesArray = event.value;
        break;
      case 'awards':
        this._awardsArray = event.value;
        break;
      case 'readingPrograms':
        this._readingProgramsArray = event.value;
        break;
      case 'formats':
        this._formatsArray = event.value;
        break;
      case 'HasOclcNumber':
        this._hasOclcNumberArray = event.value;
        break;
      case 'AcceleratedReaderInterestLevel':
        this._acceleratedReaderInterestLevel = event.value;
        break;
      case 'AcceleratedReaderReadingLevelRangeFullFacets':
        this._acceleratedReaderReadingLevel = event.value;
        break;
      case 'AcceleratedReaderTitlePointValueRangeFullFacets':
        this._acceleratedReaderTitlePointValue = event.value;
        break;
      case 'LexileRangeFullFacets':
        this._lexilesArray = event.value;
        break;
      case 'ReadingCountsReadingLevelRangeFullFacets':
        this._readingCountsReadingLevel = event.value;
        break;
      case 'ReadingCountsTitlePointValueRangeFullFacets':
        this._readingCountsTitlePointValue = event.value;
        break;
      case 'ReadingCountsInterestLevel':
        this._readingCountsInterestLevel = event.value;
        break;
      case 'pubYears':
        this._pubYearsArray = event.value;
        break;
      case 'CLCDAgeFullFacets':
        this._clcdAgeLevel = event.value;
        break;
      case 'CLCDGradesFullFacets':
        this._clcdGradeLevel = event.value;
        break;
    }

    this.filterChangeEE.emit(this.getFilters());
  }

  collapseRefine() {
    this.expandCollapse = false;
  }

  expandRefine() {
    this.expandCollapse = true;
  }

  clearAll() {
    this.advancedFilterComponents.forEach((f) => f.collapse());
    this.customAdvancedFilterComponents.forEach((f) => f.collapse());

    this._languagesArray = [];
    this._pubYearsArray = [];
    this._formatsArray = [];
    this._materialTypesArray = [];
    this._audiencesArray = [];
    this._acceleratedReaderInterestLevel = [];
    this._acceleratedReaderReadingLevel = [];
    this._acceleratedReaderTitlePointValue = [];
    this._readingCountsReadingLevel = [];
    this._readingCountsTitlePointValue = [];
    this._readingCountsInterestLevel = [];
    this._hasOclcNumberArray = [];
    this._lexilesArray = [];
    this._awardsArray = [];
    this._readingProgramsArray = [];
    this._dateItemAddedArray = [];
    this._clcdAgeLevel = [];
    this._clcdGradeLevel = [];
    this._scopingLevel = -1;
  }

  trackById(item: any): any {
    return item.id;
  }

  getFilters() {
    return {
      languages: this._languagesArray,
      pubYears: this._pubYearsArray,
      formats: this._formatsArray,
      materialTypes: this._materialTypesArray,
      forAudiences: this._audiencesArray,
      acceleratedReaderInterestLevel: this._acceleratedReaderInterestLevel,
      acceleratedReaderReadingLevel: this._acceleratedReaderReadingLevel,
      acceleratedReaderTitlePointValue: this._acceleratedReaderTitlePointValue,
      readingCountsReadingLevel: this._readingCountsReadingLevel,
      readingCountsInterestLevel: this._readingCountsInterestLevel,
      readingCountsTitlePointValue: this._readingCountsTitlePointValue,
      hasOclcNumber: this._hasOclcNumberArray,
      Lexile: this._lexilesArray,
      awards: this._awardsArray,
      readingPrograms: this._readingProgramsArray,
      DateItemsAdded: this._dateItemAddedArray,
      scopingLevel: this._scopingLevel,
      clcdAge: this._clcdAgeLevel,
      clcdGrades: this._clcdGradeLevel,
    };
  }
}
